import {
    ArrowLeftOutlined, DeleteOutlined, EditOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, ConfigProvider, Descriptions, Flex, Grid, theme as AntdTheme, Typography,
} from 'antd';
import {
    useCallback, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    objectionsMutations, objectionsQueries,
} from '@/api';
import {
    DeleteProjectModal, getActionControlStyleVars, RoleGuard,
} from '@/components';
import {
    AppRoute, DARK_THEME_HTML_BACKGROUND_COLOR, UserRole,
} from '@/constants';
import { useUIStore } from '@/stores';
import { lightTheme } from '@/themes';

export const ObjectionDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = AntdTheme.useToken();
    const { theme } = useUIStore();
    const screens = Grid.useBreakpoint();
    const isMobileDetailLayout = screens.lg === false;
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const detailCardStyles = useMemo(() => {
        if (!isMobileDetailLayout) {
            return undefined;
        }
        return {
            header: {
                paddingTop: 16,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflow: 'visible',
            },
            title: {
                display: 'block',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'unset',
                width: '100%',
                maxWidth: '100%',
            },
            body: {
                padding: 16,
            },
        };
    }, [isMobileDetailLayout]);

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);

    const { data: objection, isLoading } = useQuery(objectionsQueries.getOneObjection(id || ''));

    const deleteMutationOptions = useMemo(
        () => objection
            ? objectionsMutations.delete(objection.id)
            : objectionsMutations.delete(''),
        [objection],
    );

    const { mutate: deleteObjection, isPending: isDeleting } = useMutation(deleteMutationOptions);

    const handleDelete = useCallback(() => {
        if (objection) {
            setDeleteModalOpen(true);
        }
    }, [objection]);

    const handleDeleteConfirm = useCallback(() => {
        if (objection) {
            deleteObjection(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    navigate(AppRoute.Objections);
                },
            });
        }
    }, [objection, deleteObjection, navigate]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteModalOpen(false);
    }, []);

    return (
        <>
            <Helmet>
                <title>Деталі заперечення</title>
            </Helmet>

            <Card
                loading={isLoading}
                className="detail-page-card"
                styles={detailCardStyles}
                title={
                    <Flex
                        vertical={isMobileDetailLayout}
                        align={isMobileDetailLayout ? 'stretch' : 'center'}
                        justify="space-between"
                        gap="middle"
                        wrap="wrap"
                        style={{ width: '100%' }}
                    >
                        <Flex align="center" gap="middle" style={{ minWidth: 0, flex: isMobileDetailLayout ? undefined : 1 }}>
                            <Link
                                to={AppRoute.Objections}
                                aria-label="Назад"
                                className="app-action-control app-action-control--icon"
                                style={getActionControlStyleVars(token, {
                                    variant: 'text',
                                    bordered: false,
                                    iconOnly: true,
                                })}
                            >
                                <ArrowLeftOutlined style={{ fontSize: 16 }} />
                            </Link>
                            <Typography.Title
                                level={4}
                                ellipsis={false}
                                style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                            >
                                <span>
                                    {objection?.title || 'Деталі заперечення'}
                                </span>
                            </Typography.Title>
                        </Flex>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                            <Flex gap="middle" wrap="wrap" style={{ marginLeft: isMobileDetailLayout ? 0 : 'auto', justifyContent: isMobileDetailLayout ? 'flex-start' : undefined }}>
                                <Link
                                    to={`${AppRoute.Objections}/${id}/edit`}
                                    className="app-action-control"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'primary',
                                    })}
                                >
                                    <EditOutlined style={{ fontSize: 16 }} />
                                    Редагувати
                                </Link>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                >
                                    Видалити
                                </Button>
                            </Flex>
                        </RoleGuard>
                    </Flex>
                }
            >
                <ConfigProvider
                    theme={{
                        components: {
                            Descriptions: {
                                itemPaddingBottom: theme === 'dark' ? 16 : undefined,
                            },
                        },
                    }}
                >
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label="Назва">
                            {objection?.title || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Опис">
                            {objection?.body ? (
                                <ConfigProvider
                                    theme={{
                                        algorithm: theme === 'dark' ? AntdTheme.defaultAlgorithm : undefined,
                                        token: theme === 'dark' ? {
                                            colorText: lightThemeToken.colorText,
                                            colorLink: lightThemeToken.colorLink,
                                        } : undefined,
                                    }}
                                >
                                    <div
                                        dangerouslySetInnerHTML={{ __html: objection.body }}
                                        style={{
                                            margin: '-16px -24px',
                                            padding: '16px 24px',
                                            backgroundColor: theme === 'dark' ? DARK_THEME_HTML_BACKGROUND_COLOR : 'transparent',
                                            color: theme === 'dark' ? lightThemeToken.colorText : undefined,
                                        }}
                                    />
                                </ConfigProvider>
                            ) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Створено">
                            {objection?.created_at || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Оновлено">
                            {objection?.updated_at || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                </ConfigProvider>
            </Card>

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={objection?.title}
                entityType="objection"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />
        </>
    );
};
